(ns practitest.frontend.components.grid.subs
  (:require
   [clojure.set :as set]
   [practitest.frontend.subs :as subs]
   [re-frame.core :as re-frame]))

(re-frame/reg-sub
 ::state
 (fn [db [_ path]]
   (get-in db (cons :entity path))))

(re-frame/reg-sub
 ::selection
 (fn [[_ path]]
   [(re-frame/subscribe [::state path])])
 (fn [[state] _]
   (:selection state)))

(defn full-selection? [selection]
  (= :_all selection))

(defn empty-selection? [selection]
  (and (not (full-selection? selection))
       (empty? selection)))

(defn get-selection [db path]
  (get-in db (concat [:entity] path [:selection])))

(re-frame/reg-sub
 ::data
 (fn [[_ path]]
   [(re-frame/subscribe [::state path])])
 (fn [[state] _]
   (:data state)))

(re-frame/reg-sub
 ::history
 (fn [[_ path]]
   [(re-frame/subscribe [::state path])])
 (fn [[state] _]
   (:history state)))

(re-frame/reg-sub
 ::selected?
 (fn [[_ path _]]
   [(re-frame/subscribe [::selection path])])
 (fn [[selection] [_ _ id]]
   (or (full-selection? selection)
       (contains? selection id))))

(re-frame/reg-sub
 ::current-tab
 (fn [db [_ entity state-key]]
   (get-in db [:entity entity state-key :current-tab])))

(re-frame/reg-sub
 ::entities-current-page
 (fn [db [_ entity state-key]]
   (get-in db [:entity entity state-key :current-page])))

(re-frame/reg-sub
 ::entities-per-page
 (fn [db [_ entity state-key]]
   (get-in db [:entity entity state-key :per-page])))

(re-frame/reg-sub
 ::entities-total-count
 (fn [db [_ entity state-key]]
   (get-in db [:entity entity state-key :total-count] 0)))

(re-frame/reg-sub
 ::entities-full-data?
 (fn [db [_ entity state-key]]
   (get-in db [:entity entity state-key :full-data?])))

(re-frame/reg-sub
 ::show-link-entities-modal?
 (fn [db [_ entity state-key]]
   (get-in db [:entity entity state-key :show-link-entities-modal?])))

(re-frame/reg-sub
 ::link-entities-show-views?
 (fn [db [_ entity state-key]]
   (get-in db [:entity entity state-key :show-views?] true)))

(re-frame/reg-sub
 ::link-entities-current-filter-selected
 (fn [db [_ entity state-key]]
   (get-in db [:entity entity state-key :current-filter-selected])))

(re-frame/reg-sub
 ::current-folder-selected
 (fn [db [_ entity state-key]]
   (get-in db [:entity entity state-key :current-folder-selected])))

(re-frame/reg-sub
 ::current-view
 (fn [[_ entity state-key]]
   [(re-frame/subscribe [::current-tab entity state-key])
    (re-frame/subscribe [::link-entities-current-filter-selected entity state-key])
    (re-frame/subscribe [::current-folder-selected entity state-key])])
 (fn [[current-tab current-filter current-folder]]
   (if (= current-tab "folders")
     (assoc current-folder :type :folder)
     (assoc current-filter :type :filter))))

(re-frame/reg-sub
 ::link-entities-filters
 (fn [db [_ entity state-key default-filter-name]]
   (cons
    {:id "_DEFAULT_"
     :name default-filter-name}
    (get-in db [:entity entity state-key :filters]))))

(re-frame/reg-sub
 ::all-selected?
 (fn [[_ path]]
   [(re-frame/subscribe [::data path])
    (re-frame/subscribe [::selection path])])
 (fn [[_data selection] [_ _ page-rows]]
   (or (full-selection? selection)
       (set/subset? (set (map :id page-rows)) selection))))

(re-frame/reg-sub
 ::full-data?
 (fn [db _]
   (get-in db [:entity :testset :instances-state :full-data?])))

(defn get-table-expanded? [db current-entity state-key]
  (get-in db [current-entity state-key :expanded?] false))

(re-frame/reg-sub
 ::expanded?
 (fn [db [_ current-entity state-key]]
   (get-table-expanded? db current-entity state-key)))

(re-frame/reg-sub
 ::any-expanded?
 (fn [db [_ entity-type]]
   (let [entity-data (get db entity-type)]
     (boolean (some (fn [[_state-key state-value]]
                      (and (map? state-value)
                           (:expanded? state-value)))
                    entity-data)))))

(def fields-to-exclude-from-sorting [:actual_duration :description :preconditions :tags])

(defn field->column* [entity {:keys [name label] :as field}]
  {:field         (keyword entity name)
   :default-width 200
   :autofilter?   true
   :multiple?     false
   :sortable?     (if @(re-frame/subscribe [::full-data?])
                    true
                    (not (and (= entity :test)
                              (contains? (set fields-to-exclude-from-sorting) name))))
   :header        (if entity
                    (subs/namespaced-label entity label)
                    label)})

(defmulti field->column (fn [_ {:keys [type]}] type))

(defmethod field->column :duration [entity field]
  (merge (field->column* entity field)
         {:type :duration
          :autofilter?   false}))

(defmethod field->column :milestones [entity field]
  (merge (field->column* entity field)
         {:type :milestones
          :sortable?     false
          :autofilter?   false}))

(defmethod field->column :milestone [entity field]
  (merge (field->column* entity field)
         {:type :milestone
          :sortable?     false
          :autofilter?   false}))

(defmethod field->column :date-time [entity field]
  (merge (field->column* entity field)
         {:type :date-time
          :autofilter? true}))

(defmethod field->column :date [entity field]
  (merge (field->column* entity field)
         {:type :date
          :autofilter? true}))

(defmethod field->column :list [entity field]
  (merge (field->column* entity field)
         {:multiple? true
          :possible-values (:possible-values field)
          ;; Disabled after discussions with Ido
          ;:editable? (= :priority (:name field))
          :editable? false
          :type (if (contains? #{:run_status :run_run_status} (:name field))
                  :run_status
                  :list)}))

(defmethod field->column :linkedlist [entity field]
  (merge (field->column* entity field)
         {:type :linkedlist
          :multiple? true}))

(defmethod field->column :multilist [entity field]
  (merge (field->column* entity field)
         {:multiple? true
          :type :multilist}))

(defmethod field->column :user [entity field]
  ;; XXX multi-select
  (merge (field->column* entity field)
         {:type   :user
          :users  (:users field)
          :groups (:groups field)
          ;; Disabled after discussions with Ido
          ;:editable? (= :assigned_to (:name field))
          :editable? false}))

(defmethod field->column :tags [entity field]
  ;; XXX multi-select
  (merge (field->column* entity field)
         {:type :tags}))

(defmethod field->column :version_label [entity field]
  (merge (field->column* entity field)
         {:type :version_label
          :sortable? false
          :multiple? true
          :possible-values (:possible-values field)
          :autofilter? true}))

(defmethod field->column :number [entity field]
  (merge (field->column* entity field)
         ;; TODO: FIXTHIS
         (if (= :value_score (:name field))
           {:type :value_score}
           {:type :number})))

(defmethod field->column :memo [entity field]
  (merge (field->column* entity field)
         {:type :memo}))

(defmethod field->column :folder_id [entity field]
  (merge (field->column* entity field)
         {:type :folder_id
          :folders (:folders field)}))

(defmethod field->column :linked_milestones [entity field]
  (merge (field->column* entity field)
         {:type :linked-entities
          :sortable?     false
          :autofilter?   false}))

(defmethod field->column :text [entity field]
  (merge (field->column* entity field)
         {:type :field}))

(defmethod field->column :url [entity field]
  (merge (field->column* entity field)
         {:type :custom-field-url}))

(defmethod field->column :kanban_lane [entity field]
  (merge (field->column* entity field)
         {:type :kanban_lane}))

(defmethod field->column :default [entity field]
  (merge (field->column* entity field)
         {:type :field}))

(defmulti columns (fn [_ [_ [_ sub-entity]]]
                    sub-entity))

(defn time-management-columns [current-project]
  (when (boolean (:time_management_support current-project))
    [{:type          :duration
      :field         :test/duration_estimate
      :default-width 160
      :default-show? true
      :autofilter?   false
      :header        "Duration Estimate"}
     {:type          :duration
      :field         :last_run_duration
      :default-width 160
      :default-show? true
      :autofilter?   false
      :header        "Last Run Duration"}]))

(defn filter-out-memo?
      [fields field-key]
      (let [{:keys [type name]} (get fields field-key)]
           (not (or
                  (= :memo type)
                  (= :bdd-scenario type)
                  (= :preconditions name)))))

(defn lastrun-columns [fields entity-fields users]
      (mapv
        (fn [f]
            (let [field-data (get fields f)]
              (field->column :lastrun (cond-> field-data
                                        (= (:type field-data) :user) (assoc :users users)))))
        (->> (:run entity-fields)
             keys
             (filter (partial filter-out-memo? fields)))))

;; TODO: move to testset module ??
(defmethod columns :instances-state [[current-project entity-fields fields users groups folders test-case-versioning-enabled?] [_ [_entity _]]]
  (concat
   [{:type          :test_type
     :field         :test/test_type
     :default-width 80
     :default-show? true
     :autofilter?   true
     :text-align    "left"
     :multiple?     true
     :sortable?     false
     :header        "Type"}
    {:type          :url
     :field         :name
     :default-width 250
     :autofilter?   true
     :header        "Test Instance Name"}
    {:type          :display_id
     :field         :testset_instance_displayid
     :default-width 80
     :default-show? false
     :autofilter?   true
     :header        "ID"}
    {:type          :number
     :field         :test/display_id
     :default-width 100
     :default-show? false
     :autofilter?   true
     :header        (subs/namespaced-label :test "ID")}
    {:type          :linked-entities
     :field         :cached_linked_issues
     :default-width 110
     :default-show? true
     :autofilter?   false
     :sortable?     false
     :header        "Linked Issues"}
    {:type          :parameters
     :field         :parameters
     :default-width 100
     :default-show? false
     :autofilter?   false
     :sortable?     false
     :header        "Parameters"}]

   (when test-case-versioning-enabled?
     [{:type          :version_label
       :field         :version_label
       :default-width 150
       :default-show? true
       :autofilter?   false
       :sortable?     false
       :header        "Version Label"}])

   (time-management-columns current-project)

   ;; test fields
   (mapv #(let [field-data (get fields %)]
            (assoc
             (field->column :test (cond-> field-data
                                    (= (:type field-data) :user) (assoc :users users)
                                    (= (:type field-data) :folder_id) (assoc :folders folders)))
             ;; Hide test fields by default
             :default-show? false))
         (->> (keys (dissoc (:test entity-fields)
                            ;; Already handled
                            :duration_estimate
                            :last_run_duration
                            :test_type
                            :display_id
                            :version_label))
              (filter (partial filter-out-memo? fields))))

   ;; last run fields
   (lastrun-columns fields entity-fields users)

   ;; instance fields, ordered
   ;; Ask was to keep specific ordering of fields
   (let [first-fields [:run_status :last_run :tester :priority :assigned_to]
         rest-fields (set/difference (set (keys (:instance entity-fields)))
                                     (set first-fields))
         conf-fn (fn [c]
                   (->> c
                        (mapv (fn [field]
                                (when-let [field-data (get fields field)]
                                  (field->column nil (assoc field-data
                                                            :users users
                                                            :groups groups)))))
                        (remove #(or
                                  (nil? %)
                                  (= (:type %) :memo)))))]
     (concat
      (conf-fn first-fields)
      [{:type          :status-bar
        :field         :steps_status_bar
        :default-width 160
        :default-show? true
        :autofilter?   false
        :sortable?     false
        :header        "Steps Status Bar"}
       {:type          :number
        :field         :runs_count
        :default-width 110
        :default-show? true
        :autofilter?   false
        :sortable?     true
        :header        "Runs count"}]
      (conf-fn rest-fields)))))


;; TODO: move to testset module ??
(defmethod columns :testsets-instances-grid [[current-project entity-fields fields users groups folders test-case-versioning-enabled?] [_ [_entity _]]]
  (concat
   [{:type          :test_type
     :field         :test/test_type
     :default-width 80
     :default-show? true
     :autofilter?   true
     :text-align    "left"
     :multiple?     true
     :sortable?     false
     :header        "Type"}
    {:header        "Test Set Name"
     :field         :suite_name
     :autofilter?   true
     :default-width 200
     :width         200
     :type          :internal-url
     :sortable?     true}
    {:type          :url
     :field         :name
     :default-width 250
     :autofilter?   true
     :header        "Test Instance Name"}
    {:type          :display_id
     :field         :testset_instance_displayid
     :default-width 80
     :default-show? false
     :autofilter?   true
     :header        "ID"}
    {:type          :number
     :field         :test/display_id
     :default-width 100
     :default-show? false
     :autofilter?   true
     :header        (subs/namespaced-label :test "ID")}
    {:type          :linked-entities
     :field         :cached_linked_issues
     :default-width 110
     :default-show? true
     :autofilter?   false
     :sortable?     false
     :header        "Linked Issues"}
    {:type          :parameters
     :field         :parameters
     :default-width 100
     :default-show? false
     :autofilter?   false
     :sortable?     false
     :header        "Parameters"}]

   (when test-case-versioning-enabled?
     [{:type          :version_label
       :field         :version_label
       :default-width 150
       :default-show? true
       :autofilter?   false
       :sortable?     false
       :header        "Version Label"}])

   (time-management-columns current-project)

   ;; test fields
   (mapv #(let [field-data (get fields %)]
            (assoc
             (field->column :test (cond-> field-data
                                    (= (:type field-data) :user) (assoc :users users)
                                    (= (:type field-data) :folder_id) (assoc :folders folders)))
             :default-show? false
             :sortable? false
             :autofilter? false))
     (->> (keys (dissoc (:test entity-fields)
                        ;; Already handled
                        :duration_estimate
                        :last_run_duration
                        :test_type
                        :display_id
                        :version_label))
          (filter (partial filter-out-memo? fields))))

   ;; last run fields
   (lastrun-columns fields entity-fields users)

   ;; instance fields, ordered
   ;; Ask was to keep specific ordering of fields
   (let [first-fields [:run_status :last_run :tester :priority :assigned_to]
         rest-fields (set/difference (set (keys (:instance entity-fields)))
                                     (set first-fields))
         conf-fn (fn [c]
                   (->> c
                        (mapv (fn [field]
                                (when-let [field-data (get fields field)]
                                  (field->column nil (assoc field-data
                                                            :users users
                                                            :groups groups)))))
                        (remove #(or
                                  (nil? %)
                                  (= (:type %) :memo)))))]
     (concat
      (conf-fn first-fields)
      [{:type          :status-bar
        :field         :steps_status_bar
        :default-width 160
        :default-show? true
        :autofilter?   false
        :sortable?     false
        :header        "Steps Status Bar"}
       {:type          :number
        :field         :runs_count
        :default-width 110
        :default-show? true
        :autofilter?   false
        :sortable?     true
        :header        "Runs count"}]
      (conf-fn rest-fields)))))


(def event-types-map
  {"Label Version" {:label "Label Version"}})

(defmethod columns :history [[_ _ _ users _groups] _]
  [{:type          :user
    :autofilter?   true
    :default-width 50
    :users         users
    :field         :user_id
    :header        "User"}
   {:type          :date-time
    :autofilter?   true
    :default-width 100
    :field         :created_at
    :header        "Changed"}
   {:type          :list
    :multiple?     true
    :possible-values (vec (keys event-types-map))
    :autofilter? true,
    :default-width 150
    :field         :event_type
    :header        "Event Type"}
   {:type          :text
    :autofilter?   true
    :default-width "100%"
    :field         :log
    :header        "Log"}])

(defmethod columns :history2 [[_ _ _ users _groups] _]
  [{:type          :user
    :autofilter?   true
    :default-width 200
    :users         users
    :field         :user_id
    :header        "User"}
   {:type          :date-time
    :autofilter?   true
    :default-width 200
    :field         :created_at
    :header        "Date"}
   {:type          :list
    :multiple?     true
    :possible-values (vec (keys event-types-map))
    :autofilter? true,
    :default-width 150
    :field         :event_type
    :header        "Event Type"}
   {:type          :text
    :autofilter?   true
    :default-width "auto"
    :field         :log
    :header        "Log"}])

(defmethod columns :issues-linked-runs [[]]
  [{:type :custom-field-url,
    :field :test-name,
    :default-width 250,
    :autofilter? true,
    :header "Test Name"}
   {:type :custom-field-url,
    :field :test-set-name,
    :default-width 250,
    :autofilter? true,
    :header "Test Set Name"}
   {:type :run_status,
    :field :run_status,
    :default-width 70,
    :autofilter? true,
    :header "Run Status"}])

(defmethod columns :default [_ [_ [entity sub-entity]]]
  (js/console.log "not supported yet" [entity sub-entity])
  [])

(re-frame/reg-sub
 ::columns
 (fn [_]
   [(re-frame/subscribe [::subs/current-project])
    (re-frame/subscribe [::subs/all-entity-fields])
    (re-frame/subscribe [::subs/fields])
    (re-frame/subscribe [::subs/users])
    (re-frame/subscribe [::subs/groups])
    (re-frame/subscribe [:practitest.frontend.folders/folders])
    (re-frame/subscribe [::subs/test-case-versioning-enabled?])])
 columns)

(re-frame/reg-sub
 ::permutations-enabled?
 :<- [::subs/options]
 (fn [options _]
   (:permutations-enabled? options)))

(re-frame/reg-sub
 ::automation-enabled?
 :<- [::subs/options]
 (fn [options _]
   (:automation-enabled? options)))

(comment
  (tap> (re-frame/subscribe [::subs/all-entity-fields])))

(re-frame/reg-sub
 ::filter-cols
 (fn [db [_ entity-type state-key]]
   (get-in db [:entity entity-type state-key :filter-cols])))

;; TODO: use hierarchical subscription
(re-frame/reg-sub
  ::ptql
  (fn [db [_ entity-type state-key]]
    (get-in db [:entity entity-type state-key :ptql])))
